import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Box, Collapse } from "@mui/material";
import { Draggable, Droppable } from "@hello-pangea/dnd";
import {
  ExpandMoreOutlined,
  ExpandLessOutlined,
} from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";
import { TeamMemberWorkload } from "../../models/models";
import SidebarMemberRow from "./SidebarMemberRow";
import { getAvatarColor, getInitials } from "../shared/AssigneeAvatarStack";

// Team color palette for the dot indicator
const TEAM_COLORS = [
  "#3498DB", "#E74C3C", "#2ECC71", "#9B59B6", "#F39C12",
  "#1ABC9C", "#E67E22", "#34495E", "#16A085", "#C0392B",
];

const getTeamColor = (teamId: string): string => {
  let hash = 0;
  for (let i = 0; i < teamId.length; i++) {
    hash = ((hash << 5) - hash + teamId.charCodeAt(i)) | 0;
  }
  return TEAM_COLORS[Math.abs(hash) % TEAM_COLORS.length];
};

interface SidebarTeamRowProps {
  teamId: string;
  teamName: string;
  memberCount: number;
  members: TeamMemberWorkload[];
  draggableIndex: number;
}

const SidebarTeamRow: React.FC<SidebarTeamRowProps> = ({
  teamId,
  teamName,
  memberCount,
  members,
  draggableIndex,
}) => {
  const [expanded, setExpanded] = useState(true);
  const teamColor = getTeamColor(teamId);

  const maxOpen = members.length > 0
    ? Math.max(...members.map((m) => m.openCount), 1)
    : 1;

  // Total open WOs across all team members
  const totalWOs = members.reduce((sum, m) => sum + m.openCount, 0);

  // Stacked avatar circles for collapsed state (max 3 visible)
  const avatarSize = 22;
  const avatarOverlap = -8;
  const visibleMembers = members.slice(0, 3);

  return (
    <Box>

      {/* Team header row — draggable for team assignment */}
      <Draggable draggableId={`team-${teamId}`} index={draggableIndex}>
        {(provided, snapshot) => {
          const draggableElement = (
            <Box
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              onClick={(e) => {
                // Only toggle expand when not dragging
                if (!snapshot.isDragging) {
                  setExpanded((prev) => !prev);
                }
              }}
              sx={{
                display: "flex",
                flexDirection: snapshot.isDragging ? "column" : "row",
                alignItems: snapshot.isDragging ? "stretch" : "center",
                gap: snapshot.isDragging ? "0px" : "6px",
                padding: snapshot.isDragging ? "0px" : "6px 8px", /* spec: team row padding */
                cursor: snapshot.isDragging ? "grabbing" : "grab",
                transition: snapshot.isDragging ? "none" : "background-color 0.15s ease",
                transform: snapshot.isDragging ? "rotate(2deg)" : "none",
                boxShadow: snapshot.isDragging
                  ? "0 8px 24px rgba(0,0,0,0.18)"
                  : "none",
                opacity: snapshot.isDragging ? 0.95 : 1,
                backgroundColor: snapshot.isDragging ? "#FFFFFF" : "transparent",
                borderRadius: snapshot.isDragging ? "6px" : "6px", /* spec: 6px radius */
                border: snapshot.isDragging ? "1px solid #E8E5DF" /* spec: border */ : "none",
                minWidth: snapshot.isDragging ? "160px" : undefined,
                maxWidth: snapshot.isDragging ? "220px" : undefined,
                "&:hover": snapshot.isDragging
                  ? {}
                  : { backgroundColor: "#F7F5F0" /* spec: input-bg / selected row */ },
              }}
            >
              {/* Header part: chevron + dot + name + WO count */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: snapshot.isDragging ? "8px 10px 4px 10px" : "0px",
                  flex: snapshot.isDragging ? undefined : 1,
                  minWidth: 0,
                }}
              >
                {/* Expand/collapse chevron — hide when dragging */}
                {!snapshot.isDragging && (
                  expanded ? (
                    <ExpandLessOutlined sx={{ fontSize: 14, color: dt.textMuted, flexShrink: 0 }} />
                  ) : (
                    <ExpandMoreOutlined sx={{ fontSize: 14, color: dt.textMuted, flexShrink: 0 }} />
                  )
                )}

                {/* Team color dot */}
                <Box
                  sx={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    backgroundColor: teamColor,
                    flexShrink: 0,
                  }}
                />

                {/* Team name — spec: Sora 12px/600 text-primary */}
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#0C1117", /* spec: text-primary */
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {teamName}
                </Box>

                {/* WO count — spec: JetBrains Mono 10px/500 text-muted */}
                <Box
                  sx={{
                    fontFamily: dt.fontMono,
                    fontSize: "10px",
                    fontWeight: 500,
                    color: "#8C9099", /* spec: text-muted */
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {totalWOs}
                </Box>
              </Box>

              {/* Drag preview: show member avatars when dragging */}
              {snapshot.isDragging && members.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "4px 10px 8px 10px",
                  }}
                >
                  {/* Stacked member avatars */}
                  <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                    {members.slice(0, 5).map((member, idx) => (
                      <Box
                        key={member.userId}
                        sx={{
                          width: "22px",
                          height: "22px",
                          borderRadius: "50%",
                          backgroundColor: getAvatarColor(member.userId),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "9px",
                          fontWeight: 700,
                          fontFamily: dt.font,
                          color: "#FFFFFF",
                          flexShrink: 0,
                          marginLeft: idx === 0 ? 0 : "-6px",
                          zIndex: members.length - idx,
                          border: `2px solid ${dt.cardBg}`,
                          boxSizing: "border-box",
                        }}
                      >
                        {getInitials(member.userName)}
                      </Box>
                    ))}
                  </Box>

                  {/* Member count label */}
                  <Box
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "10px",
                      fontWeight: 500,
                      color: dt.textMuted,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {memberCount} {memberCount === 1 ? "member" : "members"}
                  </Box>
                </Box>
              )}
            </Box>
          );

          // Portal fix: render dragging element in document.body to escape
          // parent transforms, overflow, and position contexts
          if (snapshot.isDragging) {
            return createPortal(draggableElement, document.body);
          }

          return draggableElement;
        }}
      </Draggable>

      {/* Collapsed state: stacked avatars + "N members" */}
      {!expanded && members.length > 0 && (
        <Box
          onClick={() => setExpanded(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "2px 10px 8px 30px",
            cursor: "pointer",
            borderRadius: dt.radiusSm,
            transition: "background-color 0.15s ease",
            "&:hover": { backgroundColor: dt.rowHover },
          }}
        >
          {/* Stacked avatar circles */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {visibleMembers.map((member, idx) => {
              const avatarColor = getAvatarColor(member.userId);
              return (
                <Box
                  key={member.userId}
                  sx={{
                    width: `${avatarSize}px`,
                    height: `${avatarSize}px`,
                    borderRadius: "50%",
                    backgroundColor: avatarColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "9px",
                    fontWeight: 700,
                    fontFamily: dt.font,
                    color: "#FFFFFF",
                    flexShrink: 0,
                    marginLeft: idx === 0 ? 0 : `${avatarOverlap}px`,
                    zIndex: visibleMembers.length - idx,
                    border: "2px solid #FFFFFF",
                    boxSizing: "border-box",
                  }}
                >
                  {getInitials(member.userName)}
                </Box>
              );
            })}
          </Box>

          {/* "N members" text */}
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "11px",
              fontWeight: 500,
              color: dt.textMuted,
              whiteSpace: "nowrap",
            }}
          >
            {memberCount} {memberCount === 1 ? "member" : "members"}
          </Box>
        </Box>
      )}

      {/* Collapsed state: no members */}
      {!expanded && members.length === 0 && (
        <Box
          onClick={() => setExpanded(true)}
          sx={{
            padding: "2px 10px 8px 30px",
            fontFamily: dt.font,
            fontSize: "11px",
            color: dt.textMuted,
            fontStyle: "italic",
            cursor: "pointer",
            borderRadius: dt.radiusSm,
            transition: "background-color 0.15s ease",
            "&:hover": { backgroundColor: dt.rowHover },
          }}
        >
          No members
        </Box>
      )}

      {/* Expanded member list — wrapped in Droppable for DnD source */}
      <Collapse in={expanded}>
        <Droppable droppableId={`sidebar-members-${teamId}`} type="PERSON" isDropDisabled>
          {(membersProvided) => (
            <Box
              ref={membersProvided.innerRef}
              {...membersProvided.droppableProps}
              sx={{ paddingLeft: "0px" /* member indent handled in SidebarMemberRow */ }}
            >
              {members.map((member, idx) => (
                <SidebarMemberRow
                  key={`${teamId}-${member.userId}`}
                  userId={member.userId}
                  userName={member.userName}
                  openCount={member.openCount}
                  maxOpenInTeam={maxOpen}
                  draggableIndex={idx}
                  teamId={teamId}
                />
              ))}
              {members.length === 0 && (
                <Box
                  sx={{
                    padding: "6px 14px",
                    fontFamily: dt.font,
                    fontSize: "11px",
                    color: dt.textMuted,
                    fontStyle: "italic",
                  }}
                >
                  No members
                </Box>
              )}
              {membersProvided.placeholder}
            </Box>
          )}
        </Droppable>
      </Collapse>
    </Box>
  );
};

export { getTeamColor };
export default SidebarTeamRow;
